import {marketplaceApi} from '../apis/marketplace';
import store from '../../main/vue-store';
import t from '../../main/i18n';

const state = {
  isMiniCartOpen: false,
  cartLoading: false,
  cartAddingToCart: false,
  cartId: null,
  cartResponse: null,
  cartReferences: [],
  savedCartsLoading: false,
  savedCarts: [],
  orderResponse: null
};

const getters = {
  userId: () => store.getters['auth/authenticated'] ? 'current' : 'anonymous',
  cartId: state => state.cartId,
  cartEntries: state => state.cartResponse?.cartEntries,
  cartTotalUnitCount: state => state.cartResponse?.totalUnitCount,
  cartHasRestrictedMessages: state => state.cartResponse?.cartEntries.some(
    cartEntry => cartEntry?.product?.restrictedMessage.length
  ),
  cartDeliveryModes: state => {
    const cartResponse = state?.orderResponse || state?.cartResponse;
    const cartEntries = cartResponse?.orderEntries || cartResponse?.cartEntries;
    const deliveryMode = cartResponse?.deliveryMode;

    // Shipping Consolidated because deliveryMode exists at the top level of the cartResponse
    if (deliveryMode && cartEntries) {
      return `${deliveryMode.name} ${t('Estimated Delivery')}: ` +
      `${deliveryMode.estimatedDeliveryDate}<br>` +
      `${cartEntries.map(cartEntry => cartEntry?.product?.number).join(', ')}`;
    }

    // Shipping Unconsolidated because deliveryMode does NOT exist at the top level of the cartResponse
    if (cartEntries) {
      const deliveryModes = cartEntries
        .reduce((acc, cartEntry) => {
          if (!cartEntry?.deliveryMode) return acc;

          let weight = 0;
          if (cartEntry?.deliveryMode?.name === 'Standard') weight = 0;
          if (cartEntry?.deliveryMode?.name === 'Expedited - 2nd Day') weight = 1;
          if (cartEntry?.deliveryMode?.name === 'Next Day') weight = 2;

          const key = `${cartEntry?.deliveryMode?.name}:${cartEntry?.deliveryMode?.estimatedDeliveryDate}:${weight}`;
          acc[key] = cartEntry?.deliveryMode;

          return acc;
        }, {});

      return Object.keys(deliveryModes)
        .sort((a, b) => {
          // eslint-disable-next-line no-unused-vars
          const [nameA, estimatedDeliveryDateA, weightA] = a.split(':');
          // eslint-disable-next-line no-unused-vars
          const [nameB, estimatedDeliveryDateB, weightB] = b.split(':');
          return weightA - weightB;
        })
        .map(deliveryModeKey => {
          const [name, estimatedDeliveryDate] = deliveryModeKey.split(':');
          const deliveryMode = deliveryModes[deliveryModeKey];

          return '<span>' +
            `${name} ${t('Estimated Delivery Date')}: ` +
            `${estimatedDeliveryDate}<br>` +
            `${cartEntries
              .filter(cartEntry =>
                cartEntry?.deliveryMode?.code === deliveryMode?.code
                && cartEntry?.deliveryMode?.estimatedDeliveryDate === deliveryMode?.estimatedDeliveryDate
              )
              .map(cartEntry => cartEntry?.product?.number)
              .join(', ')}` +
            '</span>';
        })
        .join('');
    }

    return false;
  },
  cartTotal: state => {
    return state.orderResponse?.totalPrice?.formattedValue
      || state.cartResponse?.totalPrice?.formattedValue;
  },
  cartSubTotal: state => {
    return state.orderResponse?.subTotal?.formattedValue
      || state.cartResponse?.subTotal?.formattedValue;
  },
  cartDiscounts: state => {
    return (state.orderResponse?.orderDiscounts?.formattedValue
      || state.cartResponse?.orderDiscounts?.formattedValue) || '--';
  },
  cartCouponCode: state => {
    return state.orderResponse?.appliedCoupon || state.cartResponse?.appliedCoupon;
  },
  cartShippingTotal: state => {
    return state.orderResponse?.deliveryCost?.formattedValue
      || state.cartResponse?.deliveryCost?.formattedValue;
  },
  cartTotalTax: state => {
    return state.orderResponse?.totalTax?.formattedValue
      || state.cartResponse?.totalTax?.formattedValue;
  },
  cartTotalPriceWithTax: state => {
    return state.orderResponse?.totalPriceWithTax?.formattedValue
      || state.cartResponse?.totalPriceWithTax?.formattedValue;
  }
};

const actions = {
  async getCurrentCart({state, commit}, {
    sync = false, mergeCart = false, references = false
  } = {}) {
    commit('set_cart_loading', true);
    if (mergeCart && state?.cartId) {
      const currentCart = await marketplaceApi.getCurrentCart();
      if (currentCart.cartResponse?.guid) {
        await marketplaceApi.mergeCartWithExistingUserCart(
          state.cartId,
          currentCart.cartResponse.guid
        );
      }
    }
    commit('update_cart_data', await marketplaceApi.getCurrentCart({sync, references}));
    commit('set_cart_loading', false);
  },
  closeMiniCart({commit}) {
    commit('close_mini_cart');
  },
  async addToCart(
    {state, commit, dispatch},
    {sku, quantity, frequency, entryNumber}
  ) {
    commit('set_adding_to_cart', true);
    commit('open_mini_cart');

    if (!state.cartId) {
      state.cartId = await marketplaceApi.getOrCreateCart();
    }

    let res;
    if (entryNumber) {
      res = await marketplaceApi.updateCartEntry(sku, quantity, frequency, entryNumber);
    } else {
      res = await marketplaceApi.addToCart(sku, quantity, frequency);
    }

    await dispatch('getCurrentCart', {references: true});
    commit('set_adding_to_cart', false);

    return res;
  },
  async removeCartEntry({commit}, {entryNumber} = {}) {
    commit('set_cart_loading', true);
    await marketplaceApi.removeCartEntry(entryNumber);
    commit('update_cart_data', await marketplaceApi.getCurrentCart({references: true}));
    commit('set_cart_loading', false);
  },
  async updateCartEntryQuantity({commit}, {entryNumber, quantity, frequency = null} = {}) {
    commit('set_cart_loading', true);
    await marketplaceApi.updateCartEntryQuantity(entryNumber, quantity, frequency);
    commit('update_cart_data', await marketplaceApi.getCurrentCart({references: true}));
    commit('set_cart_loading', false);
  },
  async getSavedCarts({commit}) {
    commit('set_saved_carts_loading', true);
    await marketplaceApi.getSavedCarts()
      .then(res => commit('set_saved_carts', res.data.carts))
      .catch(() => false);
    commit('set_saved_carts_loading', false);
  },
  async deleteCurrentCart({commit}) {
    commit('set_cart_loading', true);
    await marketplaceApi.deleteCurrentCart();
    commit('reset_cart_data');
    commit('set_cart_loading', false);
  },
  async deleteCart({commit}, cartId) {
    commit('set_saved_carts_loading', true);
    await marketplaceApi.deleteCart(cartId);
    commit('set_saved_carts_loading', false);
  },
  async placeOrder({commit}, poNumber) {
    commit('auth/auth_reset_new_registration', true, {root: true});
    return await marketplaceApi.placeOrder(poNumber)
      .then(res => {
        commit('update_order_response', res.data);
        commit('reset_cart_data');
      });
  }
};

const mutations = {
  set_cart_loading(state, loading) {
    state.cartLoading = loading;
    state.orderResponse = null;
  },
  set_adding_to_cart(state, adding) {
    state.cartLoading = adding;
    state.cartAddingToCart = adding;
  },
  open_mini_cart(state) {
    state.isMiniCartOpen = true;
    document.body.classList.add('no-overflow');
  },
  close_mini_cart(state) {
    state.cartLoading = false;
    state.isMiniCartOpen = false;
    document.body.classList.remove('no-overflow');
  },
  update_cart_data(state, data) {
    state.cartId = data.cartId;
    state.cartResponse = data.cartResponse;
    state.cartReferences = data.cartReferences;
  },
  reset_cart_data(state) {
    state.cartId = null;
    state.cartResponse = null;
    state.cartReferences = [];
  },
  set_saved_carts_loading(state, loading) {
    state.savedCartsLoading = loading;
  },
  set_saved_carts(state, res) {
    state.savedCarts = res;
  },
  reset_all_data(state) {
    state.isMiniCartOpen = false;
    state.cartId = null;
    state.cartLoading = false;
    state.cartResponse = null;
    state.cartReferences = [];
    state.savedCartsLoading = false;
    state.savedCarts = [];
    document.body.classList.remove('no-overflow');
  },
  update_order_response(state, data) {
    state.orderResponse = data;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
